import React, { useEffect, useState } from "react";
import axios from "../../auth/auth";
import { FiEdit, FiTrash2, FiSearch, FiCheck, FiX, FiMail, FiDownload, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FaMapMarkerAlt } from "react-icons/fa";

const emptyQuotation = {
    quotation_number: "",
    customer_id: "",
    garage_id: "",
    service_start_date: "",
    tax_rate: 0,
    items_tax: 0,
    installation_tax: 0,
    subscription_tax: 0,
    shipping_charge: 0,
    items: [],
    installation_services: [],
    subscriptions: [],
    terms: []
};

const emptyItem = {
    master_item_id: "",
    item_name: "",
    description: "",
    quantity: "1",
    unit_price: ""
};

const emptySubscription = {
    master_subscription_id: "",
    item_name: "",
    description: "",
    monthly_price: "",
    billing_unit: "",
    is_manual_entry: false
};

const emptyInstallationService = {
    master_installation_id: "",
    installation_service_id: "",
    expense_name: "",
    expense_quantity: 1,
    price: "",
    is_manual_entry: false
};

const formatDateTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const formattedDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric"
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
    });

    return `${formattedDate} at ${formattedTime}`;
};

const formatShippingCharge = (value) => {
    // Show "TBD" when shipping charge is explicitly 0 or not provided
    const num = Number(value);
    if (!value || Number.isNaN(num) || num === 0) return "TBD";
    return `$${num.toFixed(2)}`;
};


export default function QuotationTab() {


    const [view, setView] = useState("cards");
    const [quotations, setQuotations] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [garages, setGarages] = useState([]);
    const [termsMaster, setTermsMaster] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [formData, setFormData] = useState(emptyQuotation);
    const [filteredGarages, setFilteredGarages] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [capitalItemsMaster, setCapitalItemsMaster] = useState([]);
    const [subscriptionItemsMaster, setSubscriptionItemsMaster] = useState([]);
    const [installationServicesMaster, setInstallationServicesMaster] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [customerFilter, setCustomerFilter] = useState("All");
    const [tableCustomerId, setTableCustomerId] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [currentPage, setCurrentPage] = useState(1);
    const [editingId, setEditingId] = useState(null);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectQuotationId, setRejectQuotationId] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [readyToInvoiceModalOpen, setReadyToInvoiceModalOpen] = useState(false);
    const [readyToInvoiceQuotationId, setReadyToInvoiceQuotationId] = useState(null);
    const [approvedComments, setApprovedComments] = useState("");
    const [partialInvoiceModalOpen, setPartialInvoiceModalOpen] = useState(false);
    const [partialInvoiceItems, setPartialInvoiceItems] = useState([]);
    const [partialInvoiceComments, setPartialInvoiceComments] = useState("");
    const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
    const [emailData, setEmailData] = useState({
        name: "",
        email: ""
    });
    const [sendingEmail, setSendingEmail] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [quotationToDelete, setQuotationToDelete] = useState(null);

    // LOAD INITIAL DATA

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);
                await Promise.all([
                    loadQuotations(),
                    loadCustomers(),
                    loadGarages(),
                    loadTerms(),
                    loadCapitalItemsMaster()
                ]);
            } catch (err) {
                console.error("Error loading quotation tab data", err);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);

    const loadQuotations = async () => {
        try {
            const res = await axios.get("/quotations/");
            setQuotations(res.data || []);
        } catch {
            setQuotations([]);
        }
    };

    const loadCustomers = async () => {
        const res = await axios.get("/customers/");
        setCustomers(res.data || []);
    };

    const loadGarages = async () => {
        const res = await axios.get("/garages/");
        setGarages(res.data || []);
    };

    const loadTerms = async () => {
        try {
            const res = await axios.get("/quotations/terms/master");
            setTermsMaster(res.data || []);
        } catch {
            setTermsMaster([]);
        }
    };

    // const calculateSubscriptionTotal = () => {
    //     return formData.subscriptions.reduce(
    //         (sum, s) => sum + Number(s.monthly_price || 0),
    //         0
    //     );
    // };

    const loadCapitalItemsMaster = async () => {
        try {
            const res = await axios.get("/quotations/master/capital-items");
            setCapitalItemsMaster(res.data || []);
        } catch {
            setCapitalItemsMaster([]);
        }
    };

    const loadSubscriptionItemsMaster = async (customerId, garageId) => {
        if (!customerId || !garageId) return;
        try {
            const res = await axios.get(
                "/quotations/master/subscription-items",
                {
                    params: {
                        customer_id: customerId,
                        garage_id: garageId
                    }
                }
            );
            setSubscriptionItemsMaster(res.data || []);
        } catch (err) {
            console.error("Subscription fetch error", err);
            setSubscriptionItemsMaster([]);
        }
    };

    const loadInstallationServicesMaster = async (customerId, garageId) => {
        if (!customerId || !garageId) return;
        try {
            const res = await axios.get(
                "/quotations/master/installation-services",
                {
                    params: {
                        customer_id: customerId,
                        garage_id: garageId
                    }
                }
            );
            setInstallationServicesMaster(res.data || []);
        } catch (err) {
            console.error("Installation services fetch error", err);
            setInstallationServicesMaster([]);
        }
    };

    // CUSTOMER CHANGE → FILTER GARAGES

    const handleCustomerChange = (customerId) => {
        const filtered = garages.filter(
            g => g.CustomerID === customerId
        );

        setFilteredGarages(filtered);

        setSubscriptionItemsMaster([]); 
        setInstallationServicesMaster([]); 

        setFormData({
            ...formData,
            customer_id: customerId,
            garage_id: "",
            items_tax: 0,
            installation_tax: 0,
            subscription_tax: 0
        });
    };

    const handleGarageChange = (garageId) => {

        const selectedGarage = garages.find(g => g.GarageID === garageId);

        const updatedForm = {
            ...formData,
            garage_id: garageId,
            items_tax: selectedGarage ? Number(selectedGarage.items_tax || 0) : 0,
            installation_tax: selectedGarage ? Number(selectedGarage.installation_tax || 0) : 0,
            subscription_tax: selectedGarage ? Number(selectedGarage.subscription_tax || 0) : 0
        };

        setFormData(updatedForm);

        loadSubscriptionItemsMaster(
            updatedForm.customer_id,
            garageId
        );
        loadInstallationServicesMaster(
            updatedForm.customer_id,
            garageId
        );
    };

    // ADD ITEMS

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...formData.items, { ...emptyItem }]
        });
    };

    const updateItem = (index, field, value) => {

        const updated = [...formData.items];
        updated[index][field] = value;

        setFormData({
            ...formData,
            items: updated
        });
    };

    const removeItem = (index) => {

        const updated = [...formData.items];
        updated.splice(index, 1);

        setFormData({
            ...formData,
            items: updated
        });
    };

    // ADD SUBSCRIPTIONS

    const addSubscription = () => {

        setFormData({
            ...formData,
            subscriptions: [
                ...formData.subscriptions,
                { ...emptySubscription }
            ]
        });
    };

    const updateSubscription = (index, field, value) => {

        const updated = [...formData.subscriptions];
        updated[index][field] = value;

        setFormData({
            ...formData,
            subscriptions: updated
        });
    };

    const removeSubscription = (index) => {

        const updated = [...formData.subscriptions];
        updated.splice(index, 1);

        setFormData({
            ...formData,
            subscriptions: updated
        });
    };

    // ADD INSTALLATION / LABOUR EXPENSES

    const addInstallationService = () => {
        setFormData({
            ...formData,
            installation_services: [
                ...formData.installation_services,
                { ...emptyInstallationService }
            ]
        });
    };

    const selectInstallationService = (index, masterId) => {
        const master = installationServicesMaster.find(
            (m) => m.master_installation_id === masterId
        );

        const updated = [...formData.installation_services];
        updated[index] = {
            ...updated[index],
            master_installation_id: masterId,
            installation_service_id: master?.installation_service_id || "",
            expense_name: master?.expense_name || "",
            expense_quantity: master?.expense_quantity ?? 1,
            price: master?.price ?? 0
        };

        setFormData({
            ...formData,
            installation_services: updated
        });
    };

    const updateInstallationService = (index, field, value) => {
        const updated = [...formData.installation_services];
        updated[index][field] = value;

        setFormData({
            ...formData,
            installation_services: updated
        });
    };

    const removeInstallationService = (index) => {
        const updated = [...formData.installation_services];
        updated.splice(index, 1);

        setFormData({
            ...formData,
            installation_services: updated
        });
    };

    const updateTerm = (index, value) => {

        const updated = [...formData.terms];
        updated[index].content = value;

        setFormData({
            ...formData,
            terms: updated
        });
    };

    // SAVE QUOTATION

    const hasSectionEntry = (entry, keys) =>
        keys.some((key) => entry[key] !== "" && entry[key] !== null && entry[key] !== undefined);

    const handleSave = async () => {

        if (!formData.customer_id || !formData.garage_id) {
            alert("Customer and Garage required");
            return;
        }

        // Validate that manual installation services have a name
        const invalidInstallation = formData.installation_services.some(
            service => service.is_manual_entry && (!service.expense_name || service.expense_name.trim() === "")
        );
        if (invalidInstallation) {
            alert("Labour Expenses must have a Name");
            return;
        }

        // Validate that manual subscriptions have a name
        const invalidSubscription = formData.subscriptions.some(
            sub => sub.is_manual_entry && (!sub.item_name || sub.item_name.trim() === "")
        );
        if (invalidSubscription) {
            alert("Subscription Items must have a Name");
            return;
        }

        const payload = {
            customer_id: formData.customer_id,
            garage_id: formData.garage_id,
            service_start_date: formData.service_start_date || null,
            tax_rate: Number(formData.tax_rate || 0),
            items_tax: Number(formData.items_tax || 0),
            installation_tax: Number(formData.installation_tax || 0),
            subscription_tax: Number(formData.subscription_tax || 0),
            shipping_charge: Number(formData.shipping_charge || 0),
            items: formData.items
                .filter((item) => hasSectionEntry(item, ["master_item_id", "item_name", "description", "unit_price"]))
                .map(item => ({
                    master_item_id: item.master_item_id || null,
                    item_name: item.item_name || "",
                    description: item.description || "",
                    quantity: Number(item.quantity || 1),
                    unit_price: Number(item.unit_price || 0)
                })),

            installation_services: formData.installation_services
                .filter((service) => hasSectionEntry(service, ["master_installation_id", "installation_service_id", "expense_name", "price"]))
                .map(service => ({
                    master_installation_id: service.master_installation_id || null,
                    installation_service_id: service.installation_service_id || null,
                    expense_name: service.expense_name || "",
                    expense_quantity: Number(service.expense_quantity || 1),
                    price: Number(service.price || 0),
                    is_manual_entry: service.is_manual_entry || false
                })),
            subscriptions: formData.subscriptions
                .filter((sub) => hasSectionEntry(sub, ["master_subscription_id", "item_name", "description", "monthly_price", "billing_unit"]))
                .map(sub => ({
                    master_subscription_id: sub.master_subscription_id || null,
                    item_name: sub.item_name || "",
                    description: sub.description || "",
                    monthly_price: Number(sub.monthly_price || 0),
                    billing_unit: sub.billing_unit || "",
                    is_manual_entry: sub.is_manual_entry || false
                })),
            terms: formData.terms
        };

        try {

            if (editingId) {
                //  UPDATE DRAFT
                await axios.put(
                    `/quotations/${editingId}`,
                    payload
                );
            } else {
                //  CREATE NEW
                await axios.post(
                    "/quotations/create",
                    payload
                );
            }

            setEditingId(null);
            setFormOpen(false);
            loadQuotations();

        } catch (err) {
            alert("Save failed: " + (err.response?.data?.detail || err.message));
        }
    };

    // Send mail 
    const handleSendEmail = async () => {
        if (!emailData.email || !emailData.email.includes("@")) {
            alert("Invalid email");
            return;
        }

        try {
            setSendingEmail(true);

            await axios.post(
                `/quotations/${previewData.id}/send-sow-email`,
                {
                    email: emailData.email,
                    name: emailData.name
                }
            );

            alert("Email sent successfully");

            setSendEmailModalOpen(false);
            setPreviewOpen(false);

            loadQuotations();

        } catch (err) {
            alert(err.response?.data?.detail || "Failed to send email");
        } finally {
            setSendingEmail(false);
        }
    };

    const isEmailDisabled =
        !previewData ||
        previewData.status !== "Draft" ||
        previewData.email_status === "Sent" ||
        sendingEmail;

    // OPEN FORM

    const ensureAtLeastOneRow = (data) => ({
        ...data,
        items: data.items?.length ? data.items : [{ ...emptyItem }],
        installation_services: data.installation_services?.length
            ? data.installation_services
            : [{ ...emptyInstallationService }],
        subscriptions: data.subscriptions?.length
            ? data.subscriptions
            : [{ ...emptySubscription }]
    });

    const openForm = () => {

        if (!termsMaster.length) {
            alert("Terms are still loading. Please wait.");
            return;
        }

        const mappedTerms = termsMaster.map((t, index) => ({
            title: t.title,
            content: t.content,
            display_order: t.display_order ?? index
        }));

        setEditingId(null);

        setFormData(ensureAtLeastOneRow({
            ...JSON.parse(JSON.stringify(emptyQuotation)),
            terms: mappedTerms
        }));

        setFormOpen(true);
    };

    // DELETE

    const deleteQuotation = (id) => {
        setQuotationToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const confirmDeleteQuotation = async () => {
        if (!quotationToDelete) return;

        try {
            await axios.delete(`/quotations/${quotationToDelete}`);
            loadQuotations();
            setDeleteConfirmOpen(false);
            setQuotationToDelete(null);
        } catch (error) {
            console.error("Error deleting quotation:", error);
            alert("Failed to delete quotation");
        }
    };

    const cloneQuotation = async (quotationId) => {
        try {

            const res = await axios.post(
                `/quotations/${quotationId}/clone`
            );

            // const newId = res.data.new_quotation_id;

            alert("New revision created");

            setPreviewOpen(false);
            loadQuotations();

        } catch (err) {
            alert(err.response?.data?.detail || "Clone failed");
        }
    };

    const editDraftQuotation = async (quotationId) => {
        try {

            const res = await axios.get(
                `/quotations/${quotationId}`
            );

            const data = res.data;

            setEditingId(quotationId);

            setFormData(ensureAtLeastOneRow({
                quotation_number: data.quotation_number,
                customer_id: data.customer_id,
                garage_id: data.garage_id,
                service_start_date: data.service_start_date || "",
                tax_rate: Number(data.tax_rate || 0),
                items_tax: Number(data.items_tax || 0),
                installation_tax: Number(data.installation_tax || 0),
                subscription_tax: Number(data.subscription_tax || 0),
                shipping_charge: Number(data.shipping_charge || 0),

                items: data.items.map(i => ({
                    master_item_id: i.master_item_id,
                    item_name: i.item_name,
                    description: i.description,
                    quantity: i.quantity,
                    unit_price: Number(i.unit_price)
                })),

                subscriptions: data.subscriptions.map(s => ({
                    master_subscription_id: s.master_subscription_id,
                    item_name: s.item_name,
                    monthly_price: Number(s.monthly_price),
                    billing_unit: s.billing_unit
                })),

                installation_services: (data.installation_services || []).map(s => ({
                    master_installation_id: s.master_installation_id,
                    installation_service_id: s.installation_service_id,
                    expense_name: s.expense_name,
                    expense_quantity: Number(s.expense_quantity || 0),
                    price: Number(s.price || 0)
                })),

                terms: data.terms
            }));

            const filtered = garages.filter(
                g => g.GarageID === data.garage_id
            );

            setFilteredGarages(filtered);

            await loadSubscriptionItemsMaster(data.customer_id, data.garage_id);
            await loadInstallationServicesMaster(data.customer_id, data.garage_id);

            setFormOpen(true);

        } catch {
            alert("Cannot edit quotation");
        }
    };

    const openPreview = async (id) => {
        try {
            const res = await axios.get(`/quotations/${id}`);
            setPreviewData(res.data);
            setPreviewOpen(true);
        } catch (err) {
            console.error(err);
            alert("Error loading quotation details");
        }
    };

    // CALCULATE TOTAL

    // Calculate items subtotal
    const calculateItemsSubtotal = () => {
        return formData.items.reduce(
            (sum, i) => sum + (Number(i.quantity || 0) * Number(i.unit_price || 0)),
            0
        );
    };

    // Calculate items tax
    const calculateItemsTax = () => {
        return (
            calculateItemsSubtotal() *
            (Number(formData.items_tax || 0) / 100)
        );
    };

    // Calculate items grand total (subtotal + tax)
    const calculateItemsGrandTotal = () => {
        return calculateItemsSubtotal() + calculateItemsTax();
    };

    // Calculate installation subtotal
    const calculateInstallationSubtotal = () => {
        return formData.installation_services.reduce(
            (sum, s) =>
                sum + (Number(s.expense_quantity || 0) * Number(s.price || 0)),
            0
        );
    };

    // Calculate installation tax
    const calculateInstallationTax = () => {
        return (
            calculateInstallationSubtotal() *
            (Number(formData.installation_tax || 0) / 100)
        );
    };

    // Calculate installation grand total (subtotal + tax)
    const calculateInstallationGrandTotal = () => {
        return calculateInstallationSubtotal() + calculateInstallationTax();
    };

    // Calculate subscription subtotal
    const calculateSubscriptionSubtotal = () => {
        return formData.subscriptions.reduce(
            (sum, s) => sum + Number(s.monthly_price || 0),
            0
        );
    };

    // Calculate subscription tax
    const calculateSubscriptionTax = () => {
        return (
            calculateSubscriptionSubtotal() *
            (Number(formData.subscription_tax || 0) / 100)
        );
    };

    // Calculate subscription grand total (subtotal + tax)
    const calculateSubscriptionGrandTotal = () => {
        return calculateSubscriptionSubtotal() + calculateSubscriptionTax();
    };

    // Calculate shipping charge with tax
    const calculateShippingWithTax = () => {
        const baseShipping = Number(formData.shipping_charge || 0);
        const shippingTax = baseShipping * (Number(formData.tax_rate || 0) / 100);
        return baseShipping + shippingTax;
    };

    // const calculateCapitalTotal = () => {
    //     return calculateItemsSubtotal() + calculateInstallationSubtotal();
    // };

    // CAPITAL subtotal only
    // const calculateCapitalSubtotal = () => {
    //     return calculateCapitalTotal();
    // };

    // TAX applies only to capital
    // const calculateTaxAmount = () => {
    //     return (
    //         calculateCapitalSubtotal() *
    //         (Number(formData.tax_rate || 0) / 100)
    //     );
    // };

    // Estimated total = Items Grand Total + Installation Grand Total + Subscription Grand Total + Shipping with Tax
    const calculateEstimatedTotal = () => {
        return (
            calculateItemsGrandTotal() +
            calculateInstallationGrandTotal() +
            calculateSubscriptionGrandTotal() +
            calculateShippingWithTax()
        );
    };

    const previewItemsSubtotal = (preview) => {
        if (!preview?.items?.length) return 0;
        return preview.items.reduce(
            (sum, item) => sum + (Number(item.quantity || 0) * Number(item.unit_price || 0)),
            0
        );
    };

    const previewItemsTax = (preview) => {
        return (
            previewItemsSubtotal(preview) *
            (Number(preview?.items_tax || 0) / 100)
        );
    };

    const previewItemsGrandTotal = (preview) => {
        return previewItemsSubtotal(preview) + previewItemsTax(preview);
    };

    const previewInstallationSubtotal = (preview) => {
        if (!preview?.installation_services?.length) return 0;
        return preview.installation_services.reduce(
            (sum, service) =>
                sum + (Number(service.expense_quantity || 0) * Number(service.price || 0)),
            0
        );
    };

    const previewInstallationTax = (preview) => {
        return (
            previewInstallationSubtotal(preview) *
            (Number(preview?.installation_tax || 0) / 100)
        );
    };

    const previewInstallationGrandTotal = (preview) => {
        return previewInstallationSubtotal(preview) + previewInstallationTax(preview);
    };

    const previewSubscriptionSubtotal = (preview) => {
        if (!preview?.subscriptions?.length) return 0;
        return preview.subscriptions.reduce(
            (sum, sub) => sum + Number(sub.monthly_price || 0),
            0
        );
    };

    const previewSubscriptionTax = (preview) => {
        return (
            previewSubscriptionSubtotal(preview) *
            (Number(preview?.subscription_tax || 0) / 100)
        );
    };

    const previewSubscriptionGrandTotal = (preview) => {
        return previewSubscriptionSubtotal(preview) + previewSubscriptionTax(preview);
    };

    const previewShippingCharge = (preview) => Number(preview?.shipping_charge || 0);

    const previewShippingTax = (preview) => {
        return previewShippingCharge(preview) * (Number(preview?.tax_rate || 0) / 100);
    };

    const previewShippingWithTax = (preview) => {
        return previewShippingCharge(preview) + previewShippingTax(preview);
    };

    const previewEstimatedTotal = (preview) => {
        return (
            previewItemsGrandTotal(preview) +
            previewInstallationGrandTotal(preview) +
            previewSubscriptionGrandTotal(preview) +
            previewShippingWithTax(preview)
        );
    };

    const filteredQuotations = quotations.filter(q => {

        const customer = customers.find(
            c => c.CustomerID === q.customer_id
        );

        const customerName = customer?.CustomerName || "";

        const matchesSearch =
            !searchTerm ||
            q.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customerName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesCustomer =
            customerFilter === "All" || q.customer_id === customerFilter;

        const matchesStatus =
            statusFilter === "All" || q.status === statusFilter;

        return matchesSearch && matchesCustomer && matchesStatus;

    });

    const quotationsPerPage = 12;
    const totalPages = Math.ceil(filteredQuotations.length / quotationsPerPage);
    const page = Math.min(currentPage, Math.max(totalPages, 1));
    const paginatedQuotations = filteredQuotations.slice(
        (page - 1) * quotationsPerPage,
        page * quotationsPerPage
    );

    const customerIdsWithQuotations = new Set(quotations.map(q => q.customer_id));
    const customersWithQuotations = customers.filter(c =>
        customerIdsWithQuotations.has(c.CustomerID)
    );

    const tableQuotations = quotations.filter(q =>
        tableCustomerId === "All" || q.customer_id === tableCustomerId
    );

    const updateQuotationStatus = async (quotationId, status, reason = null, partialQuote = null) => {
        try {
            await axios.put(
                `/quotations/${quotationId}/status`,
                null,
                {
                    params: {
                        status,
                        rejection_reason: reason,
                        ApprovedComments: status === "Approved" ? approvedComments.trim() || null : null,
                        partial_quote: partialQuote
                    }
                }
            );

            alert(`Quotation ${status}`);
            setPreviewOpen(false);
            setRejectModalOpen(false);
            setRejectReason("");
            setRejectQuotationId(null);
            setReadyToInvoiceModalOpen(false);
            setReadyToInvoiceQuotationId(null);
            setApprovedComments("");
            loadQuotations();

        } catch (err) {
            alert(err.response?.data?.detail || "Error updating quotation status");
        }
    };

    const openPartialInvoice = async (quotationId) => {
        try {
            const [quotationResponse, historyResponse] = await Promise.all([
                axios.get(`/quotations/${quotationId}`),
                axios.get(`/quotations/${quotationId}/partial-invoices`)
            ]);
            const deliveredByItem = {};
            (historyResponse.data || []).forEach((batch) => {
                if (batch.status === "Cancelled") return;
                (batch.items || []).forEach((item) => {
                    deliveredByItem[item.quotation_item_id] =
                        (deliveredByItem[item.quotation_item_id] || 0) + Number(item.delivered_quantity || 0);
                });
            });
            setPartialInvoiceItems((quotationResponse.data.items || []).map((item) => ({
                ...item,
                remaining_quantity: Math.max(Number(item.quantity || 0) - (deliveredByItem[item.id] || 0), 0),
                delivered_quantity: 0
            })));
            setReadyToInvoiceQuotationId(quotationId);
            setPartialInvoiceComments("");
            setPartialInvoiceModalOpen(true);
        } catch (err) {
            alert(err.response?.data?.detail || "Unable to load invoice quantities");
        }
    };

    const selectInvoiceMode = (partialQuote) => {
        const quotationId = readyToInvoiceQuotationId;
        setReadyToInvoiceModalOpen(false);
        setApprovedComments("");
        if (partialQuote) {
            openPartialInvoice(quotationId);
            return;
        }
        updateQuotationStatus(quotationId, "Approved", null, false);
        setReadyToInvoiceQuotationId(null);
    };

    const submitPartialInvoice = async () => {
        const items = partialInvoiceItems
            .filter((item) => Number(item.delivered_quantity) > 0)
            .map((item) => ({
                quotation_item_id: item.id,
                delivered_quantity: Number(item.delivered_quantity)
            }));
        if (!items.length) {
            alert("Select at least one delivered item quantity");
            return;
        }
        try {
            await axios.post(
                `/quotations/${readyToInvoiceQuotationId}/partial-invoices`,
                { items, sales_comments: partialInvoiceComments.trim() || null }
            );
            alert("Partial invoice submitted to Finance");
            setPartialInvoiceModalOpen(false);
            setReadyToInvoiceQuotationId(null);
            setPartialInvoiceItems([]);
            setPartialInvoiceComments("");
            loadQuotations();
        } catch (err) {
            alert(err.response?.data?.detail || "Unable to submit partial invoice");
        }
    };

    const downloadPdf = async (quotationId) => {
        try {

            const response = await axios.get(
                `/quotations/${quotationId}/download`,
                { responseType: "blob" }
            );

            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `quotation_${quotationId}.pdf`;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            alert("Error downloading PDF");
        }
    };

    // UI

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-600 text-sm">Loading quotations...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-2">

            {/* TOP BAR */}

            <div className="flex justify-between mb-4">
                <div className="flex gap-3">

                    {/* SEARCH */}
                    <div className="relative w-72">
                        <FiSearch className="absolute left-3 top-3 text-gray-400" />
                        <input
                            className="w-full pl-10 pr-4 py-2 border rounded-lg"
                            placeholder="Search quotations..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>

                    {/* CUSTOMER FILTER */}
                    <select
                        className="border px-4 py-2 rounded-lg"
                        value={customerFilter}
                        onChange={(e) => {
                            setCustomerFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="All">All Customers</option>
                        {customersWithQuotations.map((c) => (
                            <option key={c.CustomerID} value={c.CustomerID}>
                                {c.CustomerName}
                            </option>
                        ))}
                    </select>

                    {/* STATUS FILTER DROPDOWN */}
                    <select
                        className="border px-4 py-2 rounded-lg"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="All">All</option>
                        <option value="Draft">Draft</option>
                        <option value="Sent">Sent</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Superseded">Superseded</option>
                    </select>

                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => setView("fullTable")}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg"
                    >
                        Check Status
                    </button>

                    <button
                        onClick={() => setView("table")}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg"
                    >
                        Table View
                    </button>

                    <button
                        onClick={openForm}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                    >
                        + New Quotation
                    </button>
                    
                </div>
            </div>

            {/* CARDS */}

            {view === "cards" && (

                <div className="grid grid-cols-3 gap-4">

                    {paginatedQuotations.map((q) => (

                        <div
                            key={q.id}
                            onClick={() => openPreview(q.id)}
                            className="bg-white border border-gray-100 shadow-sm hover:shadow-md p-6 rounded-xl cursor-pointer transition-all duration-200"
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    {(() => {
                                        const customer = customers.find(
                                            c => c.CustomerID === q.customer_id
                                        );
                                        const garage = garages.find(
                                            g => g.GarageID === q.garage_id
                                        );

                                        return (
                                            <>
                                                <h2 className="text-lg font-semibold text-gray-800">
                                                    {customer?.CustomerName}
                                                </h2>

                                                <p className="text-sm text-blue-400 m-1">
                                                    {q?.garage_name}
                                                </p>

                                                <p className="text-sm text-gray-500 flex items-center gap-2 truncate">
                                                    <FaMapMarkerAlt className="text-gray-400 text-sm shrink-0" />
                                                    <span className="truncate">{customer?.Address}</span>
                                                </p>

                                                <p className="text-sm text-gray-400"> Created on {" "}
                                                    {formatDateTime(q.created_at)}
                                                </p>
                                            </>
                                        );
                                    })()}
                                </div>

                                <div className="flex items-center gap-2">
                                    {q.partial_quote === true && !q.partial_invoice_complete && (
                                        <span
                                            className="text-xs font-bold px-2 py-1 rounded bg-amber-100 text-amber-800"
                                            title="Partial quotation: additional phases are required"
                                        >
                                            PQ
                                        </span>
                                    )}
                                    <span
                                        className={`text-xs font-semibold px-3 py-1 rounded-full
                ${q.status === "Approved"
                                            ? "bg-green-100 text-green-700"
                                            : q.status === "Rejected"
                                                ? "bg-red-100 text-red-700"
                                                : q.status === "Sent"
                                                    ? "bg-blue-100 text-blue-700"
                                                    : "bg-yellow-100 text-yellow-700"
                                        }`}
                                    >
                                        {q.status === "Approved" ? "Accepted" : q.status}
                                    </span>
                                </div>
                            </div>

                            <div className="my-4">
                                <p className="text-sm text-gray-400">Estimated Total</p>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    ${Number(q.estimated_total || 0).toFixed(2)}
                                </h3>
                            </div>

                            <hr className="my-4" />

                            <div className="flex justify-end gap-3">
                                {q.status === "Draft" && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            editDraftQuotation(q.id);
                                        }}
                                        className="px-4 py-1 rounded bg-gray-100 flex items-center gap-1 hover:bg-gray-200 transition"
                                    >
                                        <FiEdit /> Edit
                                    </button>
                                )}

                                {q.status === "Rejected" && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            cloneQuotation(q.id);
                                        }}
                                        className="px-4 py-1 rounded bg-gray-100 flex items-center gap-1 hover:bg-gray-200 transition"
                                    >
                                        <FiEdit /> Revise
                                    </button>
                                )}

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteQuotation(q.id);
                                    }}
                                    className="px-4 py-1 rounded bg-red-100 text-red-600 flex items-center gap-1 hover:bg-red-200 transition"
                                >
                                    <FiTrash2 /> Delete
                                </button>
                            </div>
                        </div>

                    ))}
                </div>
            )}

            {view === "cards" && totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-6">
                    <button
                        type="button"
                        onClick={() => setCurrentPage(page - 1)}
                        disabled={page === 1}
                        className="p-2 border rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                        aria-label="Previous page"
                    >
                        <FiChevronLeft />
                    </button>

                    <span className="text-sm text-gray-600" aria-live="polite">
                        Page {page} of {totalPages}
                    </span>

                    <button
                        type="button"
                        onClick={() => setCurrentPage(page + 1)}
                        disabled={page === totalPages}
                        className="p-2 border rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                        aria-label="Next page"
                    >
                        <FiChevronRight />
                    </button>
                </div>
            )}

            {/* TABLE VIEW */}
            {(view === "table" || view === "fullTable") && (
                <div className="bg-white border border-gray-100 rounded-lg p-4">
                    {view === "fullTable" ? (
                        // STATUS CHECK TABLE
                        <>
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium">Customer:</label>
                                    <select
                                        className="border px-3 py-2 rounded-lg"
                                        value={tableCustomerId}
                                        onChange={(e) => setTableCustomerId(e.target.value)}
                                    >
                                        <option value="All">Select Customer</option>
                                        {customersWithQuotations.map((c) => (
                                            <option key={c.CustomerID} value={c.CustomerID}>
                                                {c.CustomerName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    onClick={() => setView("cards")}
                                    className="border px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                                >
                                    Back to Cards
                                </button>
                            </div>

                            {tableCustomerId === "All" ? (
                                <p className="text-sm text-gray-500">
                                    Select a customer to view their quotation status.
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm text-left">
                                        <thead>
                                            <tr className="border-b bg-gray-50">
                                                <th className="px-4 py-2">Quotation #</th>
                                                <th className="px-4 py-2">Created</th>
                                                <th className="px-4 py-2">Service Start Date</th>
                                                <th className="px-4 py-2">Status</th>
                                                <th className="px-4 py-2">email_status</th>
                                                <th className="px-4 py-2">email_sent_at</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableQuotations.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                                                        No quotations found for this customer.
                                                    </td>
                                                </tr>
                                            ) : (
                                                tableQuotations.map((q) => (
                                                    <tr
                                                        key={q.id}
                                                        className="border-b hover:bg-gray-50 cursor-pointer"
                                                        onClick={() => openPreview(q.id)}
                                                    >
                                                        <td className="px-4 py-2">
                                                            {q.quotation_number || "-"}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {formatDateTime(q.created_at)}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {q.service_start_date
                                                                ? new Date(q.service_start_date).toLocaleDateString("en-US", {
                                                                    year: "numeric",
                                                                    month: "short",
                                                                    day: "numeric"
                                                                })
                                                                : "-"
                                                            }
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {q.status}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {q.email_status}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {q.email_sent_at ? formatDateTime(q.email_sent_at) : "-"}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    ) : (
                        // FULL TABLE WITH ACTIONS
                        <>
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-medium">Customer:</label>
                                    <select
                                        className="border px-3 py-2 rounded-lg"
                                        value={tableCustomerId}
                                        onChange={(e) => setTableCustomerId(e.target.value)}
                                    >
                                        <option value="All">All Customers</option>
                                        {customersWithQuotations.map((c) => (
                                            <option key={c.CustomerID} value={c.CustomerID}>
                                                {c.CustomerName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    onClick={() => setView("cards")}
                                    className="border px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                                >
                                    Back to Cards
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm text-left">
                                    <thead>
                                        <tr className="border-b bg-gray-50">
                                            <th className="px-4 py-2">Customer Name</th>
                                            <th className="px-4 py-2">Garage Name</th>
                                            <th className="px-4 py-2">Location</th>
                                            <th className="px-4 py-2">Status</th>
                                            <th className="px-4 py-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableQuotations.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                                                    No quotations found.
                                                </td>
                                            </tr>
                                        ) : (
                                            tableQuotations.map((q) => {
                                                const customer = customers.find(c => c.CustomerID === q.customer_id);
                                                const garage = garages.find(g => g.GarageID === q.garage_id);
                                                return (
                                                    <tr key={q.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openPreview(q.id)}>
                                                        <td className="px-4 py-2">
                                                            {customer?.CustomerName || "-"}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {garage?.GarageName || "-"}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            {customer?.Address || "-"}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <span className={`text-xs font-semibold px-3 py-1 rounded-full
                                                                ${q.status === "Approved"
                                                                    ? "bg-green-100 text-green-700"
                                                                    : q.status === "Rejected"
                                                                        ? "bg-red-100 text-red-700"
                                                                        : q.status === "Sent"
                                                                            ? "bg-blue-100 text-blue-700"
                                                                            : "bg-yellow-100 text-yellow-700"
                                                                }`}>
                                                                {q.status === "Approved" ? "Accepted" : q.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <div className="flex gap-2">
                                                                {q.status === "Draft" && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            editDraftQuotation(q.id);
                                                                        }}
                                                                        className="px-2 py-1 rounded bg-gray-100 text-gray-600 flex items-center gap-1 hover:bg-gray-200 transition text-xs"
                                                                        title="Edit"
                                                                    >
                                                                        <FiEdit size={14} />
                                                                    </button>
                                                                )}

                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        deleteQuotation(q.id);
                                                                    }}
                                                                    className="px-2 py-1 rounded bg-red-100 text-red-600 flex items-center gap-1 hover:bg-red-200 transition text-xs"
                                                                    title="Delete"
                                                                >
                                                                    <FiTrash2 size={14} />
                                                                </button>

                                                                <button
                                                                    disabled={q.status === "Rejected" || q.status === "Superseded" || (q.status === "Approved" && (q.partial_quote === false || q.partial_invoice_complete))}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (q.status === "Approved" && q.partial_quote !== false && !q.partial_invoice_complete) {
                                                                            setReadyToInvoiceQuotationId(q.id);
                                                                            setReadyToInvoiceModalOpen(true);
                                                                        } else {
                                                                            updateQuotationStatus(q.id, "Approved");
                                                                        }
                                                                    }}
                                                                    className={`px-2 py-1 rounded flex items-center gap-1 transition text-xs ${
                                                                            q.status === "Rejected" || q.status === "Superseded" || (q.status === "Approved" && (q.partial_quote === false || q.partial_invoice_complete))
                                                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                            : "bg-green-100 text-green-600 hover:bg-green-200"
                                                                    }`}
                                                                    title={q.status === "Approved" ? (q.partial_quote === false || q.partial_invoice_complete ? "Quotation fully invoiced" : "Ready to Invoice") : "Accept Quotation"}
                                                                >
                                                                    <FiCheck size={14} />
                                                                </button>

                                                                <button
                                                                    disabled={q.status === "Rejected" || q.status === "Superseded" || q.status === "Approved"}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setRejectQuotationId(q.id);
                                                                        setRejectModalOpen(true);
                                                                    }}
                                                                    className={`px-2 py-1 rounded flex items-center gap-1 transition text-xs ${
                                                                        q.status === "Rejected" || q.status === "Superseded" || q.status === "Approved"
                                                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                            : "bg-red-100 text-red-600 hover:bg-red-200"
                                                                    }`}
                                                                    title="Reject Quotation"
                                                                >
                                                                    <FiX size={14} />
                                                                </button>

                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setPreviewData(q);
                                                                        setSendEmailModalOpen(true);
                                                                    }}
                                                                    className="px-2 py-1 rounded bg-blue-100 text-blue-600 flex items-center gap-1 hover:bg-blue-200 transition text-xs"
                                                                    title="Send SOW Email"
                                                                >
                                                                    <FiMail size={14} />
                                                                </button>

                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        downloadPdf(q.id);
                                                                    }}
                                                                    className="px-2 py-1 rounded bg-purple-100 text-purple-600 flex items-center gap-1 hover:bg-purple-200 transition text-xs"
                                                                    title="Download PDF"
                                                                >
                                                                    <FiDownload size={14} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* FORM */}

            {formOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center">
                    <div className="bg-white w-[1000px] p-6 rounded-xl max-h-[90vh] overflow-y-auto relative">
                        <button
                            type="button"
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                            onClick={() => setFormOpen(false)}
                        >
                            <FiX size={20} />
                        </button>

                        <h2 className="text-xl font-semibold mb-4">
                            New Quotation
                        </h2>
                        <p className="text-xs text-gray-500 mb-4">
                            Fields marked with <span className="text-red-500">*</span> are required
                        </p>

                        <div className="grid grid-cols-2 gap-4 mb-6">

                            {/* CUSTOMER SELECT */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Customer <span className="text-red-600">*</span>
                                </label>
                                <select
                                    className="border p-2 w-full rounded"
                                    value={formData.customer_id}
                                    onChange={(e) => handleCustomerChange(e.target.value)}
                                >
                                    <option value="">Select Customer</option>
                                    {customers.map((c) => (
                                        <option key={c.CustomerID} value={c.CustomerID}>
                                            {c.CustomerName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* GARAGE SELECT */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Garage <span className="text-red-600">*</span>
                                </label>

                                <select
                                    className="border p-2 w-full rounded"
                                    value={formData.garage_id}
                                    onChange={(e) => handleGarageChange(e.target.value)}
                                >
                                    <option value="">Select Garage</option>

                                    {filteredGarages.map((g) => (
                                        <option key={g.GarageID} value={g.GarageID}>
                                            {g.GarageName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* CAPITAL ITEMS */}
                        <h3 className="font-semibold mb-2">List of Items</h3>
                        <div className="grid grid-cols-7 gap-4 mb-4 items-center">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium mb-1">Name</label>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium mb-1">Item Description</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Quantity</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Actual Price ($)</label>
                            </div>
                            <div></div> {/* For remove button */}
                            {formData.items.map((item, i) => (
                                <div key={i} className="contents">
                                    <select
                                        className="border p-2 rounded col-span-2"
                                        value={item.master_item_id || ""}
                                        onChange={(e) => {

                                            const selectedId = Number(e.target.value);

                                            const master = capitalItemsMaster.find(
                                                m => m.id === selectedId
                                            );

                                            const updated = [...formData.items];

                                            updated[i] = {
                                                ...updated[i],
                                                master_item_id: selectedId,
                                                item_name: master?.item_name || "",
                                                description: master?.description || "",
                                                unit_price: master?.default_unit_price || 0
                                            };

                                            setFormData({
                                                ...formData,
                                                items: updated
                                            });
                                        }}
                                    >
                                        <option value="">Select Item</option>

                                        {capitalItemsMaster
                                            // .filter(m => {
                                            //     const selectedIds = formData.items
                                            //         .map(item => Number(item.master_item_id))
                                            //         .filter(Boolean);

                                            //     return (
                                            //         !selectedIds.includes(m.id) ||
                                            //         m.id === Number(item.master_item_id)
                                            //     );
                                            // })
                                            .map(m => (
                                                <option key={m.id} value={m.id}>
                                                    {m.item_name}
                                                </option>
                                            ))}
                                    </select>

                                    <input
                                        type="text"
                                        className="border p-2 rounded col-span-2"
                                        placeholder="Description"
                                        value={item.description}
                                        onChange={(e) => updateItem(i, "description", e.target.value)}
                                    />

                                    <input
                                        type="number"
                                        min={0}
                                        className="border p-2 rounded"
                                        placeholder="Qty"
                                        value={item.quantity}
                                        onChange={(e) => updateItem(i, "quantity", e.target.value)}
                                    />
                                    <div className="flex flex-col">
                                        <input
                                            type="number"
                                            min={0}
                                            className="border p-2 rounded"
                                            value={item.unit_price}
                                            onChange={(e) =>
                                                updateItem(i, "unit_price", e.target.value)
                                            }
                                        />
                                        <span className="text-xs text-gray-700 mt-1">
                                            Suggested Price: ${Number(
                                                capitalItemsMaster.find(m => m.id === item.master_item_id)
                                                    ?.default_unit_price || 0
                                            ).toFixed(2)}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="h-[42px] w-[42px] flex items-center justify-center col-span-1
                                            border border-red-200 text-red-600 
                                            rounded hover:bg-red-50 transition"
                                        onClick={() => removeItem(i)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Items Tax (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="border p-2 rounded w-full"
                                    value={formData.items_tax}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            items_tax: e.target.value === "" ? "" : Number(e.target.value)
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <button
                            onClick={addItem}
                            className="mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                        >
                            + Add Item
                        </button>

                        <p className="mb-6 font-semibold">
                            Items Subtotal: ${calculateItemsSubtotal().toFixed(2)}
                        </p>
                        <p className="mb-6 font-semibold">
                            Items Tax ({formData.items_tax}%): ${calculateItemsTax().toFixed(2)}
                        </p>
                        <p className="mb-6 font-bold text-lg">
                            Items Grand Total: ${calculateItemsGrandTotal().toFixed(2)}
                        </p>

                        {/* INSTALLATION / LABOUR EXPENSES */}
                        <h3 className="font-semibold mb-2">Installation / Labour Expenses</h3>
                        <div className="grid grid-cols-5 gap-4 mb-4 items-center">
                            <div>
                                <label className="block text-sm font-medium mb-1">Name <span className="text-red-600">*</span></label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Qty</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Price ($)</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Manual Entry</label>
                            </div>
                            <div></div> {/* For remove button */}

                            {formData.installation_services.map((service, i) => (
                                <React.Fragment key={i}>
                                    {service.is_manual_entry ? (
                                        <>
                                            <input
                                                type="text"
                                                className="border p-2 rounded"
                                                placeholder="Enter Name"
                                                value={service.expense_name}
                                                required
                                                onChange={(e) =>
                                                    updateInstallationService(i, "expense_name", e.target.value)
                                                }
                                            />
                                        </>
                                    ) : (
                                        <select
                                            className="border p-2 rounded"
                                            value={service.master_installation_id || ""}
                                            required
                                            onChange={(e) => {
                                                const selectedId = Number(e.target.value);
                                                selectInstallationService(i, selectedId);
                                            }}
                                        >
                                            <option value="">Select Expense</option>

                                            {installationServicesMaster
                                                .filter((m) => {
                                                    const selectedIds = formData.installation_services
                                                        .map((item) => Number(item.master_installation_id))
                                                        .filter(Boolean);

                                                    return (
                                                        !selectedIds.includes(m.master_installation_id) ||
                                                        m.master_installation_id === Number(service.master_installation_id)
                                                    );
                                                })
                                                .map((m) => (
                                                    <option
                                                        key={m.master_installation_id}
                                                        value={m.master_installation_id}
                                                    >
                                                        {m.expense_name}
                                                    </option>
                                                ))}
                                        </select>
                                    )}

                                    <input
                                        type="number"
                                        min={0}
                                        className="border p-2 rounded"
                                        placeholder="Qty"
                                        value={service.expense_quantity}
                                        onChange={(e) =>
                                            updateInstallationService(i, "expense_quantity", e.target.value)
                                        }
                                    />

                                    <input
                                        type="number"
                                        min={0}
                                        className="border p-2 rounded"
                                        placeholder="Price"
                                        value={service.price}
                                        onChange={(e) =>
                                            updateInstallationService(i, "price", e.target.value)
                                        }
                                    />

                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 cursor-pointer"
                                        checked={service.is_manual_entry || false}
                                        onChange={(e) =>
                                            updateInstallationService(i, "is_manual_entry", e.target.checked)
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="h-[42px] w-[42px] flex items-center justify-center col-span-1
                                            border border-red-200 text-red-600 
                                            rounded hover:bg-red-50 transition"
                                        onClick={() => removeInstallationService(i)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Installation Tax (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="border p-2 rounded w-full"
                                    value={formData.installation_tax}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            installation_tax: e.target.value === "" ? "" : Number(e.target.value)
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <button
                            onClick={addInstallationService}
                            className="mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                        >
                            + Add Installation / Labour Expense
                        </button>

                        <p className="mb-6 font-semibold">
                            Installation Subtotal: ${calculateInstallationSubtotal().toFixed(2)}
                        </p>
                        <p className="mb-6 font-semibold">
                            Installation Tax ({formData.installation_tax}%): ${calculateInstallationTax().toFixed(2)}
                        </p>
                        <p className="mb-6 font-bold text-lg">
                            Installation Grand Total: ${calculateInstallationGrandTotal().toFixed(2)}
                        </p>

                        {/* SUBSCRIPTION ITEMS */}
                        <h3 className="font-semibold mb-2">Subscription Items (Monthly)</h3>
                        <div className="grid grid-cols-5 gap-4 mb-4 items-center">
                            <div>
                                <label className="block text-sm font-medium mb-1">Name <span className="text-red-600">*</span></label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Monthly Price ($)</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Billing Unit</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Manual Entry</label>
                            </div>
                            <div></div> {/* For remove button */}
                            {formData.subscriptions.map((sub, i) => (
                                <div key={i} className="contents">
                                    {sub.is_manual_entry ? (
                                        <>
                                            <input
                                                type="text"
                                                className="border p-2 rounded"
                                                placeholder="Enter Name"
                                                value={sub.item_name}
                                                required
                                                onChange={(e) =>
                                                    updateSubscription(i, "item_name", e.target.value)
                                                }
                                            />
                                        </>
                                    ) : (
                                        <select
                                            className="border p-2 rounded"
                                            value={sub.master_subscription_id || ""}
                                            required
                                            onChange={(e) => {

                                                const selectedId = Number(e.target.value);

                                                const master = subscriptionItemsMaster.find(
                                                    m => m.master_subscription_id === selectedId
                                                );

                                                const updated = [...formData.subscriptions];

                                                updated[i] = {
                                                    ...updated[i],
                                                    master_subscription_id: selectedId,
                                                    item_name: master?.item_name || "",
                                                    monthly_price: master?.monthly_price || 0,
                                                    billing_unit: master?.billing_unit || ""
                                                };

                                                setFormData({
                                                    ...formData,
                                                    subscriptions: updated
                                                });
                                            }}
                                        >
                                            <option value="">Select Subscription</option>

                                            {subscriptionItemsMaster
                                                .filter(m => {
                                                    const selectedIds = formData.subscriptions
                                                        .map(sub => Number(sub.master_subscription_id))
                                                        .filter(Boolean);

                                                    return (
                                                        !selectedIds.includes(m.id) ||
                                                        m.master_subscription_id === Number(sub.master_subscription_id)
                                                    );
                                                })
                                                .map(m => (
                                                    <option key={m.master_subscription_id} value={m.master_subscription_id}>
                                                        {m.item_name}
                                                    </option>
                                                ))}
                                        </select>
                                    )}

                                    <input
                                        type="number"
                                        min={0}
                                        className="border p-2 rounded"
                                        placeholder="Monthly Price"
                                        value={sub.monthly_price}
                                        onChange={(e) =>
                                            updateSubscription(i, "monthly_price", e.target.value)
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="border p-2 rounded"
                                        placeholder="Billing Unit (e.g. per garage)"
                                        value={sub.billing_unit}
                                        onChange={(e) =>
                                            updateSubscription(i, "billing_unit", e.target.value)
                                        }
                                    />
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 cursor-pointer"
                                        checked={sub.is_manual_entry || false}
                                        onChange={(e) =>
                                            updateSubscription(i, "is_manual_entry", e.target.checked)
                                        }
                                    />
                                    <button
                                        type="button"
                                        className="h-[42px] w-[42px] flex items-center justify-center 
                                            border border-red-200 text-red-600 
                                            rounded hover:bg-red-50 transition"
                                        onClick={() => removeSubscription(i)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Subscription Tax (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="border p-2 rounded w-full"
                                    value={formData.subscription_tax}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            subscription_tax: e.target.value === "" ? "" : Number(e.target.value)
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <button
                            onClick={addSubscription}
                            className="mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                        >
                            + Add Subscription
                        </button>

                        <p className="mb-6 font-semibold">
                            Subscription Subtotal: ${calculateSubscriptionSubtotal().toFixed(2)}
                        </p>
                        <p className="mb-6 font-semibold">
                            Subscription Tax ({formData.subscription_tax}%): ${calculateSubscriptionTax().toFixed(2)}
                        </p>
                        <p className="mb-6 font-bold text-lg">
                            Subscription Grand Total: ${calculateSubscriptionGrandTotal().toFixed(2)}
                        </p>

                        <div className="grid grid-cols-3 gap-4 mb-6">

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Shipping Charge ($)
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    className="border p-2 rounded w-full"
                                    value={formData.shipping_charge}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            shipping_charge: e.target.value
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Tax Rate (%)
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    className="border p-2 rounded w-full"
                                    value={formData.tax_rate}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            tax_rate: e.target.value
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="mb-6 font-semibold space-y-1">
                            <p>Items Grand Total: ${calculateItemsGrandTotal().toFixed(2)}</p>

                            <p>Installation Grand Total: ${calculateInstallationGrandTotal().toFixed(2)}</p>

                            <p>Subscription Grand Total: ${calculateSubscriptionGrandTotal().toFixed(2)}</p>

                            <p>Shipping Charge (inc. {formData.tax_rate}% tax): ${calculateShippingWithTax().toFixed(2)}</p>

                            <p className="text-lg font-bold">
                                Estimated Total: ${calculateEstimatedTotal().toFixed(2)}
                            </p>
                        </div>

                        <div className="mb-6">
                            <label className="block font-semibold mb-1">
                                Service Start Date 
                            </label>
                            <input
                                type="date"
                                className="border p-2 rounded"
                                value={formData.service_start_date}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        service_start_date: e.target.value
                                    })
                                }
                            />
                        </div>

                        {formData.terms.map((t, i) => (
                            <div key={i} className="mb-4">
                                <label className="block font-semibold mb-1">
                                    {i === 0
                                        ? "Terms of Subscription and Termination:"
                                        : i === 1
                                            ? "Payment Terms:"
                                            : t.title}
                                </label>

                                <textarea
                                    className="border w-full p-2 rounded"
                                    value={t.content}
                                    onChange={(e) => updateTerm(i, e.target.value)}
                                    rows={6}
                                />
                            </div>
                        ))}

                        {/* SAVE */}

                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setFormOpen(false)}
                                className="bg-gray-200 px-4 py-2 rounded"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleSave}
                                className="bg-blue-600 text-white px-4 py-2 rounded"
                            >
                                Save Quotation
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PREVIEW MODAL */}

            {previewOpen && previewData && (

                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white w-[900px] max-h-[95vh] overflow-y-auto p-10 rounded-lg shadow-xl relative">
                        <button
                            type="button"
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                            onClick={() => setPreviewOpen(false)}
                        >
                            <FiX size={20} />
                        </button>

                        {/* HEADER */}

                        <div className="text-center mb-8">
                            <h2 className="text-lg font-semibold">
                                Subscription Services Order Form
                            </h2>

                            <p className="text-sm text-gray-600">
                                ParkEngage Inc.
                            </p>
                            <p className="text-sm text-gray-600">
                                4100 Moorpark Ave., Suite 203, San Jose, CA 95117
                            </p>
                        </div>

                        <p className="mt-2">
                            Status:
                            <span className={`ml-2 font-semibold ${previewData.status === "Approved"
                                ? "text-green-600"
                                : previewData.status === "Rejected"
                                    ? "text-red-600"
                                    : "text-yellow-600"
                                }`}>
                                {previewData.status}
                            </span>
                        </p>

                        {previewData.rejection_reason && (
                            <p className="mt-2 text-red-600">
                                <strong>Rejection Reason:</strong> {previewData.rejection_reason}
                            </p>
                        )}

                        {/* CUSTOMER INFO */}

                        <div className="mb-6">
                            <h3 className="font-semibold mb-2">
                                Customer Information:
                            </h3>

                            <p>
                                {customers.find(c => c.CustomerID === previewData.customer_id)?.CustomerName}
                            </p>

                            <p>
                                {customers.find(c => c.CustomerID === previewData.customer_id)?.Address}
                            </p>
                        </div>

                        {/* FACILITY INFO */}

                        <div className="mb-6">

                            <h3 className="font-semibold mb-2">
                                Facility Information:
                            </h3>

                            <table className="w-full border">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="border p-2 text-left">Facility Name</th>
                                        <th className="border p-2 text-left">Address</th>
                                        <th className="border p-2 text-center">Capacity</th>
                                        <th className="border p-2 text-center"># Entries</th>
                                        <th className="border p-2 text-center"># Exits</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {(() => {
                                        const garage = garages.find(
                                            g => g.GarageID === previewData.garage_id
                                        );

                                        if (!garage) return null;

                                        return (
                                            <tr>
                                                <td className="border p-2">
                                                    {garage.GarageName}
                                                </td>
                                                <td className="border p-2">
                                                    {garage.Address}
                                                </td>
                                                <td className="border p-2 text-center">
                                                    {garage.SizeSpaces ?? "-"}
                                                </td>
                                                <td className="border p-2 text-center">
                                                    {garage.EntryGates ?? garage.NumberOfEntries ?? "-"}
                                                </td>
                                                <td className="border p-2 text-center">
                                                    {garage.ExitGates ?? garage.NumberOfExits ?? "-"}
                                                </td>
                                            </tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>

                        {/* CAPITAL ITEMS */}

                        <h3 className="font-semibold mb-2">
                            Capital Expense
                        </h3>

                        <table className="w-full border mb-6">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border p-2 text-left">Item</th>
                                    <th className="border p-2 text-right">Qty</th>
                                    <th className="border p-2 text-right">Unit Price</th>
                                    <th className="border p-2 text-right">Total</th>
                                </tr>
                            </thead>

                            <tbody>
                                {previewData.items?.map((item, i) => (
                                    <tr key={i}>
                                        <td className="border p-2">
                                            <div>{item.item_name}</div>
                                            <div className="text-xs text-gray-500">{item.description}</div>
                                        </td>
                                        <td className="border p-2 text-right">{item.quantity}</td>
                                        <td className="border p-2 text-right">
                                            ${Number(item.unit_price).toFixed(2)}
                                        </td>
                                        <td className="border p-2 text-right">
                                            ${Number(item.total_price ?? item.quantity * item.unit_price).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                            <tfoot className="bg-gray-50">
                                <tr>
                                    <td className="border p-2 text-right font-medium" colSpan={3}>
                                        Items Subtotal
                                    </td>
                                    <td className="border p-2 text-right">
                                        ${previewItemsSubtotal(previewData).toFixed(2)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="border p-2 text-right font-medium" colSpan={3}>
                                        Items Tax ({previewData.items_tax || 0}%)
                                    </td>
                                    <td className="border p-2 text-right">
                                        ${previewItemsTax(previewData).toFixed(2)}
                                    </td>
                                </tr>
                                <tr className="font-semibold">
                                    <td className="border p-2 text-right font-medium" colSpan={3}>
                                        Items Total
                                    </td>
                                    <td className="border p-2 text-right">
                                        ${previewItemsGrandTotal(previewData).toFixed(2)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>

                        {/* SUBSCRIPTIONS */}
                        {previewData.subscriptions?.length > 0 && (
                            <>
                                <h3 className="font-semibold mb-2">
                                    Subscription Option
                                </h3>

                                <table className="w-full border mb-6">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="border p-2 text-left">Item</th>
                                            <th className="border p-2 text-right">Monthly Price</th>
                                            <th className="border p-2 text-right">Billing Unit</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {previewData.subscriptions.map((sub, i) => (
                                            <tr key={i}>
                                                <td className="border p-2">{sub.item_name}</td>
                                                <td className="border p-2 text-right">
                                                    ${Number(sub.monthly_price).toFixed(2)}
                                                </td>
                                                <td className="border p-2 text-right">{sub.billing_unit}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                <tfoot className="bg-gray-50">
                                    <tr>
                                        <td className="border p-2 text-right font-medium" colSpan={2}>
                                            Subscription Subtotal
                                        </td>
                                        <td className="border p-2 text-right">
                                            ${previewSubscriptionSubtotal(previewData).toFixed(2)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="border p-2 text-right font-medium" colSpan={2}>
                                            Subscription Tax ({previewData.subscription_tax || 0}%)
                                        </td>
                                        <td className="border p-2 text-right">
                                            ${previewSubscriptionTax(previewData).toFixed(2)}
                                        </td>
                                    </tr>
                                    <tr className="font-semibold">
                                        <td className="border p-2 text-right font-medium" colSpan={2}>
                                            Subscription Total
                                        </td>
                                        <td className="border p-2 text-right">
                                            ${previewSubscriptionGrandTotal(previewData).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                                </table>
                            </>
                        )}

                        {/* INSTALLATION / LABOUR EXPENSES */}
                        {previewData.installation_services?.length > 0 && (
                            <>
                                <h3 className="font-semibold mb-2">
                                    Installation / Labour Expenses
                                </h3>

                                <table className="w-full border mb-6">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="border p-2 text-left">Expense</th>
                                            <th className="border p-2 text-right">Qty</th>
                                            <th className="border p-2 text-right">Price</th>
                                            <th className="border p-2 text-right">Total</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {previewData.installation_services.map((inst, i) => {
                                            const qty = Number(inst.expense_quantity || 0);
                                            const price = Number(inst.price || 0);
                                            return (
                                                <tr key={i}>
                                                    <td className="border p-2">{inst.expense_name}</td>
                                                    <td className="border p-2 text-right">{qty}</td>
                                                    <td className="border p-2 text-right">
                                                        ${price.toFixed(2)}
                                                    </td>
                                                    <td className="border p-2 text-right">
                                                        ${(qty * price).toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                <tfoot className="bg-gray-50">
                                    <tr>
                                        <td className="border p-2 text-right font-medium" colSpan={3}>
                                            Installation Subtotal
                                        </td>
                                        <td className="border p-2 text-right">
                                            ${previewInstallationSubtotal(previewData).toFixed(2)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="border p-2 text-right font-medium" colSpan={3}>
                                            Installation Tax ({previewData.installation_tax || 0}%)
                                        </td>
                                        <td className="border p-2 text-right">
                                            ${previewInstallationTax(previewData).toFixed(2)}
                                        </td>
                                    </tr>
                                    <tr className="font-semibold">
                                        <td className="border p-2 text-right font-medium" colSpan={3}>
                                            Installation Total
                                        </td>
                                        <td className="border p-2 text-right">
                                            ${previewInstallationGrandTotal(previewData).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                                </table>
                            </>
                        )}

                        <h3 className="font-semibold mb-2">Totals</h3>
                        <table className="w-full border mb-6">
                            <tbody>
                                <tr>
                                    <td className="border p-2 text-right">Shipping Charge</td>
                                    <td className="border p-2 text-right">
                                        {formatShippingCharge(previewData.shipping_charge)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="border p-2 text-right">Shipping Tax ({previewData.tax_rate || 0}%)</td>
                                    <td className="border p-2 text-right">
                                        ${previewShippingTax(previewData).toFixed(2)}
                                    </td>
                                </tr>
                                <tr className="font-bold text-lg">
                                    <td className="border p-2 text-right">Grand Total</td>
                                    <td className="border p-2 text-right">
                                        ${previewEstimatedTotal(previewData).toFixed(2)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>



                        {/* SERVICE START */}

                        <div className="mb-6">
                            <strong>Service Start Date:</strong>{" "}
                            {previewData.service_start_date
                                ? new Date(previewData.service_start_date).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric"
                                })
                                : "Not specified"
                            }
                        </div>

                        {/* SIGNATURE SECTION */}
                        <div className="mt-16 grid grid-cols-2 gap-16">

                            {/* LEFT SIDE - CUSTOMER REPRESENTATIVE */}
                            <div>
                                <p className="font-semibold underline mb-6">
                                    {previewData.customer_name}
                                </p>

                                {/* Signature Line */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Signature</p>
                                </div>

                                {/* Printed Name */}
                                <div className="mb-6">
                                    <p className="mb-1">
                                        {/* {previewData.representative_name || ""} */}
                                    </p>
                                    <div className="border-b border-gray-600"></div>
                                    <p className="italic text-sm mt-1">Printed Name</p>
                                </div>

                                {/* Title */}
                                <div className="mb-6">
                                    <p className="mb-1">
                                        {previewData.representative_title || ""}
                                    </p>
                                    <div className="border-b border-gray-600"></div>
                                    <p className="italic text-sm mt-1">Title</p>
                                </div>

                                {/* Date */}
                                <div>
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Date</p>
                                </div>
                            </div>

                            {/* RIGHT SIDE - EMPTY */}
                            <div>
                                <p className="font-semibold underline mb-6">
                                    ParkEngage Inc.
                                </p>

                                {/* Signature */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Signature</p>
                                </div>

                                {/* Printed Name */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Printed Name</p>
                                </div>

                                {/* Title */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Title</p>
                                </div>

                                {/* Date */}
                                <div>
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Date</p>
                                </div>
                            </div>
                        </div>

                        {/* TERMS */}

                        <h3 className="font-semibold mb-2">
                            Terms and Conditions
                        </h3>

                        <div className="text-sm space-y-3 mb-6">

                            {previewData.terms
                                ?.sort((a, b) => a.display_order - b.display_order)
                                .map((term, i) => (

                                    <div key={i}>
                                        <strong>{term.title}</strong>
                                        <p>{term.content}</p>
                                    </div>
                                ))}
                        </div>

                        <div className="flex justify-between mt-6">

                            <div className="flex gap-3">

                                <button
                                    disabled={previewData.status === "Rejected" || previewData.status === "Superseded" || (previewData.status === "Approved" && (previewData.partial_quote === false || previewData.partial_invoice_complete))}
                                    onClick={() => {
                                        if (previewData.status === "Approved" && previewData.partial_quote !== false && !previewData.partial_invoice_complete) {
                                            setReadyToInvoiceQuotationId(previewData.id);
                                            setReadyToInvoiceModalOpen(true);
                                        } else {
                                            updateQuotationStatus(previewData.id, "Approved");
                                        }
                                    }}
                                    className={`px-5 py-2 rounded text-white ${previewData.status === "Rejected" || previewData.status === "Superseded" || (previewData.status === "Approved" && (previewData.partial_quote === false || previewData.partial_invoice_complete))
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-600 hover:bg-green-700"
                                        }`}
                                >
                                    {previewData.status === "Approved" ? "Ready to Invoice" : "Accept Quotation"}
                                </button>

                                <button
                                    disabled={previewData.status === "Rejected" || previewData.status === "Superseded" || previewData.status === "Approved"}
                                    onClick={() => {
                                        setRejectQuotationId(previewData.id);
                                        setRejectModalOpen(true);
                                    }}
                                    className={`px-5 py-2 rounded text-white ${previewData.status === "Rejected" || previewData.status === "Superseded" || previewData.status === "Approved"
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-red-600 hover:bg-red-700"
                                        }`}
                                >
                                    Reject Quotation
                                </button>
                            </div>

                            <div className="flex gap-3">
                                {/* Send Email Button */}
                                <button
                                    disabled={isEmailDisabled}
                                    onClick={() => {
                                        if (!previewData) return;

                                        setEmailData({
                                            name: previewData.representative_name || "",
                                            email: previewData.representative_email || ""
                                        });
                                        setSendEmailModalOpen(true);
                                    }}
                                    className={`px-4 py-2 rounded text-white ${isEmailDisabled
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-600 hover:bg-green-700"
                                        }`}
                                >
                                    {previewData?.email_status === "Sent"
                                        ? "Email Sent"
                                        : sendingEmail
                                            ? "Sending..."
                                            : previewData?.status !== "Approved"
                                                ? "Send Sow Email"
                                                : "Email Sent"}
                                </button>

                                <button
                                    onClick={() => downloadPdf(previewData.id)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                                >
                                    Download PDF
                                </button>

                                <button
                                    onClick={() => setPreviewOpen(false)}
                                    className="bg-gray-300 px-4 py-2 rounded"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Send Email Preview  */}
            {sendEmailModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white w-[500px] p-6 rounded-lg">

                        <h3 className="text-lg font-semibold mb-4">
                            Send SOW Email
                        </h3>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">
                                Representative Name
                            </label>
                            <input
                                className="border w-full p-2 rounded"
                                value={emailData.name}
                                onChange={(e) =>
                                    setEmailData({ ...emailData, name: e.target.value })
                                }
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">
                                Email
                            </label>
                            <input
                                className="border w-full p-2 rounded"
                                value={emailData.email}
                                onChange={(e) =>
                                    setEmailData({ ...emailData, email: e.target.value })
                                }
                            />
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setSendEmailModalOpen(false)}
                                className="bg-gray-300 px-4 py-2 rounded"
                            >
                                Cancel
                            </button>

                            <button
                                disabled={sendingEmail}
                                onClick={handleSendEmail}
                                className="bg-green-600 text-white px-4 py-2 rounded"
                            >
                                {sendingEmail ? "Sending..." : "Confirm & Send"}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {rejectModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white w-[500px] p-6 rounded-lg">

                        <h3 className="text-lg font-semibold mb-4">
                            Reason for Rejection
                        </h3>

                        <textarea
                            className="border w-full p-2 rounded mb-4"
                            rows={4}
                            placeholder="Enter rejection reason..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setRejectModalOpen(false);
                                    setRejectReason("");
                                    setRejectQuotationId(null);
                                }}
                                className="bg-gray-300 px-4 py-2 rounded"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={() => {
                                    if (!rejectReason.trim()) {
                                        alert("Rejection reason is required");
                                        return;
                                    }
                                    updateQuotationStatus(rejectQuotationId, "Rejected", rejectReason);
                                }}
                                className="bg-red-600 text-white px-4 py-2 rounded"
                            >
                                Confirm Rejection
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {readyToInvoiceModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white w-[500px] p-6 rounded-lg">
                        <h3 className="text-lg font-semibold mb-3">
                            Ready to Invoice
                        </h3>

                        <p className="text-sm text-gray-700 mb-4">
                            Choose how this accepted quotation should be invoiced.
                        </p>

                        <div className="flex gap-3 mb-6">
                            {quotations.find((quotation) => quotation.id === readyToInvoiceQuotationId)?.partial_quote !== true && (
                                <button
                                    onClick={() => selectInvoiceMode(false)}
                                    className="flex-1 bg-green-600 text-white px-4 py-3 rounded hover:bg-green-700"
                                >
                                    Complete Quotation
                                </button>
                            )}
                            <button
                                onClick={() => selectInvoiceMode(true)}
                                className="flex-1 bg-blue-600 text-white px-4 py-3 rounded hover:bg-blue-700"
                            >
                                Partial Quotation
                            </button>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setReadyToInvoiceModalOpen(false);
                                    setReadyToInvoiceQuotationId(null);
                                    setApprovedComments("");
                                }}
                                className="bg-gray-300 px-4 py-2 rounded"
                            >
                                Cancel
                            </button>

                        </div>
                    </div>
                </div>
            )}

            {partialInvoiceModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white w-[760px] max-w-[95vw] p-6 rounded-lg">
                        <h3 className="text-lg font-semibold mb-1">Ready to Invoice</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Enter the quantity delivered in this phase. Items left at zero will not be included.
                        </p>

                        <div className="border rounded overflow-hidden mb-4">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="p-2 text-left">Item</th>
                                        <th className="p-2 text-right">Approved</th>
                                        <th className="p-2 text-right">Remaining</th>
                                        <th className="p-2 text-right">Invoice Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {partialInvoiceItems.map((item) => (
                                        <tr key={item.id} className="border-t">
                                            <td className="p-2">{item.item_name}</td>
                                            <td className="p-2 text-right">{item.quantity}</td>
                                            <td className="p-2 text-right">{item.remaining_quantity}</td>
                                            <td className="p-2 text-right">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={item.remaining_quantity}
                                                    value={item.delivered_quantity}
                                                    disabled={item.remaining_quantity === 0}
                                                    onChange={(e) => setPartialInvoiceItems((current) => current.map((currentItem) =>
                                                        currentItem.id === item.id
                                                            ? { ...currentItem, delivered_quantity: e.target.value }
                                                            : currentItem
                                                    ))}
                                                    className="border rounded px-2 py-1 w-24 text-right disabled:bg-gray-100"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <label className="block text-sm font-medium mb-1" htmlFor="partial-sales-comments">
                            Sales comments <span className="font-normal text-gray-500">(optional)</span>
                        </label>
                        <textarea
                            id="partial-sales-comments"
                            className="border w-full p-2 rounded mb-4"
                            rows={3}
                            placeholder="Add delivery or invoicing comments..."
                            value={partialInvoiceComments}
                            onChange={(e) => setPartialInvoiceComments(e.target.value)}
                        />

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setPartialInvoiceModalOpen(false);
                                    setReadyToInvoiceQuotationId(null);
                                    setPartialInvoiceItems([]);
                                    setPartialInvoiceComments("");
                                }}
                                className="bg-gray-300 px-4 py-2 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitPartialInvoice}
                                className="bg-green-600 text-white px-4 py-2 rounded"
                            >
                                Send to Finance
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirmOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white w-[500px] p-6 rounded-lg">
                        <h3 className="text-lg font-semibold mb-4">
                            Delete Quotation
                        </h3>

                        <p className="text-gray-700 mb-6">
                            Are you sure you want to delete this quotation? This action cannot be undone.
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setDeleteConfirmOpen(false);
                                    setQuotationToDelete(null);
                                }}
                                className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={confirmDeleteQuotation}
                                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
}